/**
 * RBAC rights-profiles — frontend types (HOMCO-326, T0 — catalogue v2 / HOMCO-1227).
 *
 * The permission model mirrors the backend catalogue v2 (PR #442):
 *   apps/backend-homere-connect/src/rights-profiles/domains/entities/permission-catalogue.ts
 *   apps/backend-homere-connect/src/rights-profiles/application/dto/catalogue-line-response.dto.ts
 *   apps/backend-homere-connect/src/rights-profiles/application/dto/rights-profile-response.dto.ts
 *   apps/backend-homere-connect/src/rights-profiles/application/dto/create-rights-profile.dto.ts
 *
 * `LineId` / `PermissionAction` / `ResolvedPermissions` are shared with the read
 * layer (`features/permissions/permissionTypes.ts`) so the matrix screen and
 * `can()` read from one mirror. The matrix never recopies the catalogue tree: it
 * is fetched from `GET /rights-profiles/catalogue` (see `rights-profiles.api.ts`).
 *
 * Frontend idiom: `const` arrays + `(typeof X)[number]` unions rather than TS `enum`s.
 */

import type {
  LineId,
  PermissionAction,
  ResolvedPermissions,
} from '../../permissions/permissionTypes';
import type { PaginationMeta } from '../../../components/ui/tableTemplate/TableTemplateTypes';

export { PERMISSION_ACTIONS } from '../../permissions/permissionTypes';
export type { LineId, PermissionAction, ResolvedPermissions };

/** Display grouping of the matrix (backend `CatalogueModule`), in display order. */
export const CATALOGUE_MODULES = ['common', 'employees', 'leaves', 'payroll', 'my_ngo'] as const;
export type CatalogueModule = (typeof CATALOGUE_MODULES)[number];

/** Nature of a line (backend `CatalogueLevel`) — drives matrix indentation + prefix. */
export const CATALOGUE_LEVELS = ['interface', 'fiche', 'onglet', 'composant', 'entree'] as const;
export type CatalogueLevel = (typeof CATALOGUE_LEVELS)[number];

/**
 * One line of `GET /rights-profiles/catalogue` — `CatalogueLineResponseDto`.
 * `actions` lists the applicable actions in display order; any action absent from
 * it is shown « — » in the matrix and refused (400) by the API.
 */
export interface CatalogueLine {
  line: LineId;
  module: CatalogueModule;
  level: CatalogueLevel;
  /** Empty for a root line; a granted line requires `display` on at least one parent. */
  parents: LineId[];
  actions: PermissionAction[];
}

/**
 * Response of `GET /rights-profiles(/:id)` — `RightsProfileResponseDto`. `permissions`
 * is the flat effective-rights map (same format as `GET /auth/me`): one key per
 * catalogue line with its granted actions in catalogue order, a line with nothing
 * granted absent.
 */
export interface RightsProfile {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  permissions: ResolvedPermissions;
  /** System profile (Super Admin): read-only, cannot be modified or deleted. */
  is_locked: boolean;
  /** Platform system profile — permissions are the full catalogue, computed at read. */
  is_system: boolean;
  assigned_users_count: number;
  created_at: string; // ISO 8601 (Date serialized over the wire)
  updated_at: string; // ISO 8601
}

/** Columns `GET /rights-profiles` can sort on (HOMCO-1413) — backend `SORTABLE_COLUMNS`. */
export type RightsProfileSortBy = 'name' | 'assigned_users_count';

/**
 * Query of `GET /rights-profiles` (HOMCO-1413). `name` is sent as the `name[like]`
 * filter (case- and accent-insensitive substring), omitted when blank. Without
 * `sortBy` the server sorts by name ascending.
 */
export interface RightsProfilesQueryParams {
  page?: number;
  limit?: number;
  sortBy?: RightsProfileSortBy;
  sortOrder?: 'asc' | 'desc';
  name?: string;
}

/** Response of `GET /rights-profiles` — `PaginatedRightsProfileResponseDto`. */
export interface PaginatedRightsProfilesResponse {
  data: RightsProfile[];
  meta: PaginationMeta;
}

/** One flat permission entry in a create/update payload — `PermissionDto`. */
export interface PermissionPayload {
  line: LineId;
  action: PermissionAction;
  enabled: boolean;
}

/**
 * Body of `POST /rights-profiles` — `CreateRightsProfileDto`. `permissions` is
 * optional (the backend generates display-only defaults when omitted); the update
 * endpoint accepts a `Partial<CreateRightsProfilePayload>`.
 */
export interface CreateRightsProfilePayload {
  name: string;
  description?: string | null;
  permissions?: PermissionPayload[];
}

/** Per-line editing state in the matrix: the checked flag of each applicable action. */
export type LinePermissions = Partial<Record<PermissionAction, boolean>>;

/** The matrix editing value: one entry per catalogue line. */
export type PermissionMatrix = Partial<Record<LineId, LinePermissions>>;
